import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Share2,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  PhoneCall,
  Search,
  CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";

const rentProperties = [
  {
    id: "goyal-orchid",
    title: "Goyal Orchid",
    location: "Whitefield, Bangalore, Karnataka",
    price: "₹45,000",
    bhk: "3 BHK",
    beds: 3,
    baths: 2,
    area: "1,200 ft²",
    furnishing: "Semi-Furnished",
    deposit: "₹2,00,000",
    image:
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=85",
    url: "/goyal-orchid",
    description:
      "Modern 3 BHK apartment with spacious balcony, modular kitchen, power backup, and world-class clubhouse amenities.",
  },
  {
    id: "folium-by-sumadhura",
    title: "Folium by Sumadhura",
    location: "Siddapura, Whitefield, Bangalore",
    price: "₹73,000",
    bhk: "3 BHK",
    beds: 3,
    baths: 3,
    area: "1,542 ft²",
    furnishing: "Fully Furnished",
    deposit: "₹3,50,000",
    image:
      "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1200&q=85",
    url: "/sumadhura-edition",
    description:
      "Serene lakefront luxury residence with premium wood interiors, smart home fittings, and tranquil landscaped vistas.",
  },
  {
    id: "prestige-waterford",
    title: "Prestige Waterford",
    location: "ECC Road, Whitefield, Bangalore",
    price: "₹92,000",
    bhk: "4 BHK",
    beds: 4,
    baths: 3,
    area: "1,800 ft²",
    furnishing: "Fully Furnished",
    deposit: "₹4,50,000",
    image:
      "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=1200&q=85",
    url: "/rivera-edition",
    description:
      "Expansive 4 BHK family apartment right near ITPL with resort-style pool, tennis courts, and high-floor panoramic views.",
  },
  {
    id: "riviera-north-edition",
    title: "Riviera North Edition",
    location: "Soukya Road, Whitefield, Bengaluru",
    price: "₹65,000",
    bhk: "3 BHK",
    beds: 3,
    baths: 2,
    area: "1,450 ft²",
    furnishing: "Semi-Furnished",
    deposit: "₹3,00,000",
    image:
      "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1200&q=85",
    url: "/riviera-north-edition",
    description:
      "Contemporary villa-style residence with private terrace access, solar water heating, and 24/7 manned security.",
  },
  {
    id: "sanjeevini-heliconia",
    title: "Sanjeevini Heliconia",
    location: "Soukya Road, Whitefield, Bengaluru",
    price: "₹42,000",
    bhk: "2 BHK",
    beds: 2,
    baths: 2,
    area: "1,175 ft²",
    furnishing: "Semi-Furnished",
    deposit: "₹1,80,000",
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=85",
    url: "/sanjeevini-heliconia",
    description:
      "Brand new 2 BHK residential apartment with zero wasted space, excellent natural light, and serene garden views.",
  },
  {
    id: "brigade-granada",
    title: "Brigade Granada Executive",
    location: "Seegahalli Road, East Bengaluru",
    price: "₹55,000",
    bhk: "3 BHK",
    beds: 3,
    baths: 3,
    area: "1,500 ft²",
    furnishing: "Semi-Furnished",
    deposit: "₹2,50,000",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=85",
    url: "/brigade-granada",
    description:
      "Executive apartment in a mixed-use integrated township with retail arcade, Olympic-length pool, and quick metro transit.",
  },
];

const bhkFilters = ["All", "2 BHK", "3 BHK", "4 BHK"];

const Rent = () => {
  const [selectedBhk, setSelectedBhk] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredProperties = rentProperties.filter((property) => {
    const matchesBhk =
      selectedBhk === "All" || property.bhk === selectedBhk;
    const matchesSearch =
      property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.furnishing.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesBhk && matchesSearch;
  });

  const handleShare = (property, e) => {
    e.preventDefault();
    e.stopPropagation();

    const message = `🏠 *${property.title}*

📍 Location: ${property.location}
💰 Rent: ${property.price} / month
🛏 Bedrooms: ${property.beds} (${property.bhk})
🛁 Bathrooms: ${property.baths}
📐 Area: ${property.area}
🛋 Furnishing: ${property.furnishing}
💵 Deposit: ${property.deposit}

🔗 Property Link:
${window.location.origin}${property.url}

Hi HousyHunt, I'm interested in renting this property. Please let me know if it's available for visit.`;

    const whatsappURL = `https://wa.me/918088687429?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappURL, "_blank");
  };

  return (
    <div className="w-full bg-[#FAFCFF] min-h-screen">
      {/* ================= HERO HEADER ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-red-50/80 via-white to-[#FAFCFF] pt-12 pb-16 px-4 sm:px-6 lg:px-14 border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 bg-red-100/70 text-red-600 border border-red-200/80 px-4 py-2 rounded-full text-xs sm:text-sm font-bold tracking-wide uppercase mb-4 shadow-sm">
              <Sparkles size={15} />
              Properties for Rent in Bengaluru
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 tracking-tight leading-[1.15] max-w-4xl">
              Curated Rental Homes,{" "}
              <span className="text-red-600">Verified & Ready to Move In.</span>
            </h1>

            <p className="mt-5 text-gray-600 text-base sm:text-lg max-w-2xl leading-relaxed">
              Find premium apartments, penthouses, and gated community homes
              across Whitefield, Indiranagar, and East Bengaluru with standard
              agreements and fast move-in support.
            </p>

            {/* SEARCH */}
            <div className="mt-8 w-full max-w-2xl">
              <div className="relative w-full">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search by community, location (e.g. Whitefield), or furnishing..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition shadow-sm text-sm"
                />
              </div>
            </div>

            {/* BHK FILTERS */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              {bhkFilters.map((bhk) => (
                <button
                  key={bhk}
                  onClick={() => setSelectedBhk(bhk)}
                  className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    selectedBhk === bhk
                      ? "bg-red-600 text-white shadow-md shadow-red-500/20"
                      : "bg-white text-gray-700 border border-gray-200 hover:border-red-300 hover:text-red-600"
                  }`}
                >
                  {bhk}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= PROPERTY CARDS GRID ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-14 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900">
              Verified Rental Properties
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Showing {filteredProperties.length} rental listings in prime Bengaluru
            </p>
          </div>

          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-red-600 hover:text-red-700"
          >
            <PhoneCall size={16} /> Instant Rental Assistance on WhatsApp
          </a>
        </div>

        {filteredProperties.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
            <Bed size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-bold text-gray-800">
              No rental properties match your search
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              Try resetting your filters or search keywords.
            </p>
            <button
              onClick={() => {
                setSelectedBhk("All");
                setSearchQuery("");
              }}
              className="mt-5 px-6 py-2.5 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group"
              >
                {/* IMAGE CONTAINER */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />

                  {/* GRADIENT OVERLAY */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

                  {/* TOP BADGES */}
                  <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                    <span className="bg-white/95 backdrop-blur-md text-gray-900 text-xs font-bold px-3 py-1 rounded-full shadow">
                      {item.furnishing}
                    </span>

                    <button
                      type="button"
                      onClick={(e) => handleShare(item, e)}
                      aria-label={`Share ${item.title}`}
                      className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-md hover:bg-white text-gray-700 hover:text-black flex items-center justify-center shadow transition-all duration-200 hover:scale-105"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>

                  {/* BOTTOM RENT BADGE */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-red-200 font-semibold">
                        Monthly Rent
                      </span>
                      <p className="text-2xl font-black drop-shadow-sm">
                        {item.price}{" "}
                        <span className="text-xs font-normal text-white/80">
                          / mo
                        </span>
                      </p>
                    </div>

                    <span className="text-xs font-semibold bg-red-600/90 text-white px-3 py-1 rounded-full shadow">
                      {item.bhk}
                    </span>
                  </div>
                </div>

                {/* CONTENT */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-1">
                    {item.title}
                  </h3>

                  <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-2">
                    <MapPin size={14} className="text-red-500 shrink-0" />
                    <span className="truncate">{item.location}</span>
                  </div>

                  <p className="text-gray-600 text-xs mt-3 line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>

                  {/* SPECS GRID */}
                  <div className="grid grid-cols-3 gap-2 mt-5 p-3 rounded-2xl bg-gray-50 border border-gray-100 text-xs text-center text-gray-700">
                    <div>
                      <div className="flex items-center justify-center gap-1 text-red-600 mb-0.5">
                        <Bed size={14} />
                      </div>
                      <span className="font-bold">{item.beds} Beds</span>
                    </div>

                    <div>
                      <div className="flex items-center justify-center gap-1 text-red-600 mb-0.5">
                        <Bath size={14} />
                      </div>
                      <span className="font-bold">{item.baths} Baths</span>
                    </div>

                    <div>
                      <div className="flex items-center justify-center gap-1 text-red-600 mb-0.5">
                        <Square size={14} />
                      </div>
                      <span className="font-bold">{item.area}</span>
                    </div>
                  </div>

                  {/* ACTION BUTTONS */}
                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-3">
                    <Link
                      to={item.url}
                      className="flex-1 py-3 px-4 bg-gray-900 hover:bg-red-600 text-white rounded-xl font-semibold text-xs transition-colors duration-300 flex items-center justify-center gap-1.5 group/btn"
                    >
                      <span>Explore</span>
                      <ArrowRight
                        size={14}
                        className="group-hover/btn:translate-x-1 transition-transform"
                      />
                    </Link>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(
                          "https://wa.me/message/MDG2ZSZNG6P6G1",
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      className="py-3 px-5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-xs transition-colors shadow-md hover:shadow-red-500/20"
                    >
                      Contact
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* ================= RENTAL BENEFITS ================= */}
      <section className="bg-white py-16 px-4 sm:px-6 lg:px-14 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100 flex items-start gap-4">
              <div className="p-3 bg-red-100 text-red-600 rounded-2xl shrink-0">
                <CheckCircle2 size={24} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Standard Rental Agreement
                </h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Transparent agreements with fair security deposit terms and
                  standard notice periods.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100 flex items-start gap-4">
              <div className="p-3 bg-red-100 text-red-600 rounded-2xl shrink-0">
                <ShieldCheck size={24} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Direct Owner Connect
                </h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Every listed apartment is verified with verified owners and
                  active society permissions.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100 flex items-start gap-4">
              <div className="p-3 bg-red-100 text-red-600 rounded-2xl shrink-0">
                <Sparkles size={24} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Move-in Services
                </h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Deep cleaning, painting, and BESCOM/electricity transfer support
                  included.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Rent;
